import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Search,
  MoreHorizontal,
  ShieldCheck,
  ShieldOff,
  Ban,
  CheckCircle2,
  KeyRound,
  CalendarPlus,
  Trash2,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Crown,
} from "lucide-react";
import {
  adminListUsers,
  adminSetUserBanned,
  adminSetUserRole,
  adminSetUserPlan,
  adminExtendSubscription,
  adminSendPasswordReset,
  adminDeleteUser,
} from "@/lib/admin.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/users")({
  component: UsersPage,
});

type SortKey = "created_at" | "last_seen_at" | "email" | "plan";

type FilterChip = "all" | "active" | "banned" | "admin" | "trial" | "paid" | "free";

const PLANS = ["free", "pro", "elite"] as const;

const PAGE_SIZE = 25;

function churnDot(lastSeenAt: string | null) {
  if (!lastSeenAt) return { color: "bg-zinc-500", label: "Never seen" };
  const days = (Date.now() - new Date(lastSeenAt).getTime()) / (1000 * 60 * 60 * 24);
  if (days < 3) return { color: "bg-emerald-500", label: "Active" };
  if (days < 14) return { color: "bg-amber-500", label: "Idle" };
  return { color: "bg-rose-500", label: "At risk" };
}

function planBadgeClass(plan: string) {
  switch (plan) {
    case "elite":
      return "border-amber-400/50 text-amber-400";
    case "pro":
      return "border-primary/50 text-primary";
    default:
      return "border-border/60 text-muted-foreground";
  }
}

function fmtDate(s: string | null) {
  if (!s) return "—";
  return new Date(s).toLocaleDateString();
}

function fmtRelative(s: string | null) {
  if (!s) return "Never";
  const ms = Date.now() - new Date(s).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(s).toLocaleDateString();
}

type User = NonNullable<Awaited<ReturnType<typeof adminListUsers>>>["users"][number];

function UsersPage() {
  const qc = useQueryClient();
  const list = useServerFn(adminListUsers);
  const setRole = useServerFn(adminSetUserRole);
  const setBanned = useServerFn(adminSetUserBanned);
  const setPlan = useServerFn(adminSetUserPlan);
  const extend = useServerFn(adminExtendSubscription);
  const reset = useServerFn(adminSendPasswordReset);
  const del = useServerFn(adminDeleteUser);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "users"],
    queryFn: () => list(),
  });

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<FilterChip>("all");
  const [sortKey, setSortKey] = useState<SortKey>("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [confirm, setConfirm] = useState<
    | null
    | { kind: "delete"; user: User }
    | { kind: "ban"; user: User; banned: boolean }
    | { kind: "bulk-ban"; ids: string[] }
    | { kind: "bulk-delete"; ids: string[] }
  >(null);

  const [extendFor, setExtendFor] = useState<User | null>(null);
  const [extendDays, setExtendDays] = useState(7);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "users"] });

  const mutate = <T,>(fn: (vars: T) => Promise<unknown>, successMsg: string) =>
    useMutation({
      mutationFn: fn,
      onSuccess: () => {
        toast.success(successMsg);
        invalidate();
      },
      onError: (e: Error) => toast.error(e.message),
    });

  const roleMut = mutate(
    (v: { userId: string; role: "admin" | "user"; grant: boolean }) => setRole({ data: v }),
    "Role updated",
  );
  const banMut = mutate(
    (v: { userId: string; banned: boolean }) => setBanned({ data: v }),
    "User status updated",
  );
  const planMut = mutate(
    (v: { userId: string; plan: "free" | "pro" | "elite" }) => setPlan({ data: v }),
    "Plan updated",
  );
  const extendMut = mutate(
    (v: { userId: string; days: number }) => extend({ data: v }),
    "Subscription extended",
  );
  const resetMut = useMutation({
    mutationFn: (email: string) => reset({ data: { email } }),
    onSuccess: (r) => {
      if (r?.link) {
        navigator.clipboard?.writeText(r.link).catch(() => {});
        toast.success("Recovery link copied to clipboard");
      } else {
        toast.success("Password reset email sent");
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const deleteMut = mutate((v: { userId: string }) => del({ data: v }), "User deleted");

  const filtered = useMemo(() => {
    let rows = data?.users ?? [];
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (u) =>
          (u.email ?? "").toLowerCase().includes(q) ||
          (u.display_name ?? "").toLowerCase().includes(q) ||
          (u.full_name ?? "").toLowerCase().includes(q) ||
          u.id.toLowerCase().includes(q),
      );
    }
    switch (filter) {
      case "active":
        rows = rows.filter((u) => !u.banned);
        break;
      case "banned":
        rows = rows.filter((u) => u.banned);
        break;
      case "admin":
        rows = rows.filter((u) => u.roles.includes("admin"));
        break;
      case "trial":
        rows = rows.filter((u) => u.subscription?.status === "trialing");
        break;
      case "paid":
        rows = rows.filter((u) => u.plan === "pro" || u.plan === "elite");
        break;
      case "free":
        rows = rows.filter((u) => u.plan === "free");
        break;
    }
    rows = [...rows].sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      const av = (a[sortKey] ?? "") as string;
      const bv = (b[sortKey] ?? "") as string;
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return rows;
  }, [data, search, filter, sortKey, sortDir]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(k);
      setSortDir("desc");
    }
  };

  const allOnPageSelected =
    pageRows.length > 0 && pageRows.every((u) => selected.has(u.id));
  const toggleAllOnPage = () => {
    const next = new Set(selected);
    if (allOnPageSelected) pageRows.forEach((u) => next.delete(u.id));
    else pageRows.forEach((u) => next.add(u.id));
    setSelected(next);
  };
  const toggleOne = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const runBulkBan = async (ids: string[], banned: boolean) => {
    await Promise.allSettled(
      ids.map((userId) => setBanned({ data: { userId, banned } })),
    );
    toast.success(`${ids.length} user${ids.length === 1 ? "" : "s"} ${banned ? "banned" : "unbanned"}`);
    setSelected(new Set());
    invalidate();
  };
  const runBulkDelete = async (ids: string[]) => {
    const results = await Promise.allSettled(
      ids.map((userId) => del({ data: { userId } })),
    );
    const ok = results.filter((r) => r.status === "fulfilled").length;
    toast.success(`${ok}/${ids.length} users deleted`);
    setSelected(new Set());
    invalidate();
  };

  const counts = useMemo(() => {
    const rows = data?.users ?? [];
    return {
      all: rows.length,
      active: rows.filter((u) => !u.banned).length,
      banned: rows.filter((u) => u.banned).length,
      admin: rows.filter((u) => u.roles.includes("admin")).length,
      trial: rows.filter((u) => u.subscription?.status === "trialing").length,
      paid: rows.filter((u) => u.plan === "pro" || u.plan === "elite").length,
      free: rows.filter((u) => u.plan === "free").length,
    };
  }, [data]);

  const filters: { id: FilterChip; label: string; count: number }[] = [
    { id: "all", label: "All", count: counts.all },
    { id: "active", label: "Active", count: counts.active },
    { id: "trial", label: "Trial", count: counts.trial },
    { id: "paid", label: "Paid", count: counts.paid },
    { id: "free", label: "Free", count: counts.free },
    { id: "admin", label: "Admins", count: counts.admin },
    { id: "banned", label: "Banned", count: counts.banned },
  ];

  return (
    <div className="space-y-4">
      <Card className="border-border/60 bg-card/60 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-sm font-semibold">User management</h2>
            <p className="text-xs text-muted-foreground">
              {filtered.length} of {data?.users.length ?? 0} accounts
            </p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder="Search by email, name, or id…"
              className="h-9 pl-8 text-xs"
            />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {filters.map((f) => (
            <button
              key={f.id}
              onClick={() => {
                setFilter(f.id);
                setPage(0);
              }}
              className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                filter === f.id
                  ? "border-primary/50 bg-primary/15 text-primary"
                  : "border-border/60 bg-card text-muted-foreground hover:text-foreground"
              }`}
            >
              {f.label} <span className="opacity-60">·</span> {f.count}
            </button>
          ))}
        </div>
        {selected.size > 0 && (
          <div className="mt-3 flex items-center justify-between gap-3 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs">
            <span className="font-medium text-primary">{selected.size} selected</span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setConfirm({ kind: "bulk-ban", ids: [...selected] })}
              >
                <Ban className="mr-1 h-3 w-3" /> Ban
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => runBulkBan([...selected], false)}
              >
                <CheckCircle2 className="mr-1 h-3 w-3" /> Unban
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setConfirm({ kind: "bulk-delete", ids: [...selected] })}
              >
                <Trash2 className="mr-1 h-3 w-3" /> Delete
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
                Clear
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Card className="overflow-hidden border-border/60 bg-card/60">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-left text-[11px] uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="w-10 px-3 py-2">
                  <Checkbox
                    checked={allOnPageSelected}
                    onCheckedChange={toggleAllOnPage}
                    aria-label="Select all on page"
                  />
                </th>
                <th
                  className="cursor-pointer px-4 py-2 select-none"
                  onClick={() => toggleSort("email")}
                >
                  <span className="inline-flex items-center gap-1">
                    User <ArrowUpDown className="h-3 w-3 opacity-50" />
                  </span>
                </th>
                <th
                  className="cursor-pointer px-4 py-2 select-none"
                  onClick={() => toggleSort("plan")}
                >
                  <span className="inline-flex items-center gap-1">
                    Plan <ArrowUpDown className="h-3 w-3 opacity-50" />
                  </span>
                </th>
                <th className="px-4 py-2">Status</th>
                <th
                  className="cursor-pointer px-4 py-2 select-none"
                  onClick={() => toggleSort("last_seen_at")}
                >
                  <span className="inline-flex items-center gap-1">
                    Last seen <ArrowUpDown className="h-3 w-3 opacity-50" />
                  </span>
                </th>
                <th
                  className="cursor-pointer px-4 py-2 select-none"
                  onClick={() => toggleSort("created_at")}
                >
                  <span className="inline-flex items-center gap-1">
                    Joined <ArrowUpDown className="h-3 w-3 opacity-50" />
                  </span>
                </th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              )}
              {!isLoading && pageRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-muted-foreground">
                    No users match your filters
                  </td>
                </tr>
              )}
              {pageRows.map((u) => {
                const isAdmin = u.roles.includes("admin");
                const dot = churnDot(u.last_seen_at);
                return (
                  <tr key={u.id} className="border-t border-border/40 hover:bg-muted/20">
                    <td className="px-3 py-3">
                      <Checkbox
                        checked={selected.has(u.id)}
                        onCheckedChange={() => toggleOne(u.id)}
                        aria-label={`Select ${u.email}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span
                          title={dot.label}
                          className={`inline-block h-2 w-2 rounded-full ${dot.color}`}
                        />
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 truncate font-medium">
                            {u.display_name ?? u.full_name ?? "—"}
                            {isAdmin && (
                              <Crown className="h-3 w-3 text-amber-400" aria-label="Admin" />
                            )}
                          </div>
                          <div className="truncate text-xs text-muted-foreground">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Select
                        value={u.plan}
                        onValueChange={(v) =>
                          planMut.mutate({
                            userId: u.id,
                            plan: v as "free" | "pro" | "elite",
                          })
                        }
                      >
                        <SelectTrigger className={`h-7 w-24 text-xs ${planBadgeClass(u.plan)}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PLANS.map((p) => (
                            <SelectItem key={p} value={p} className="text-xs capitalize">
                              {p}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      {u.banned ? (
                        <Badge variant="destructive">Banned</Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="border-emerald-500/50 text-emerald-500"
                        >
                          {u.subscription?.status === "trialing" ? "Trial" : "Active"}
                        </Badge>
                      )}
                    </td>
                    <td
                      className="px-4 py-3 text-xs text-muted-foreground"
                      title={u.last_seen_at ?? ""}
                    >
                      {fmtRelative(u.last_seen_at)}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {fmtDate(u.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          <DropdownMenuLabel className="text-xs">
                            {u.email}
                          </DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() =>
                              roleMut.mutate({
                                userId: u.id,
                                role: "admin",
                                grant: !isAdmin,
                              })
                            }
                          >
                            {isAdmin ? (
                              <>
                                <ShieldOff className="mr-2 h-3.5 w-3.5" /> Revoke admin
                              </>
                            ) : (
                              <>
                                <ShieldCheck className="mr-2 h-3.5 w-3.5" /> Make admin
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setExtendDays(7);
                              setExtendFor(u);
                            }}
                          >
                            <CalendarPlus className="mr-2 h-3.5 w-3.5" /> Extend subscription
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => u.email && resetMut.mutate(u.email)}
                            disabled={!u.email}
                          >
                            <KeyRound className="mr-2 h-3.5 w-3.5" /> Reset password
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() =>
                              setConfirm({ kind: "ban", user: u, banned: !u.banned })
                            }
                          >
                            {u.banned ? (
                              <>
                                <CheckCircle2 className="mr-2 h-3.5 w-3.5" /> Unban
                              </>
                            ) : (
                              <>
                                <Ban className="mr-2 h-3.5 w-3.5" /> Ban
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => setConfirm({ kind: "delete", user: u })}
                          >
                            <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete user
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-border/60 px-4 py-2 text-xs text-muted-foreground">
          <span>
            Page {safePage + 1} of {pageCount}
          </span>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-7 w-7 p-0"
              disabled={safePage === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 w-7 p-0"
              disabled={safePage >= pageCount - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Extend subscription dialog */}
      <AlertDialog open={!!extendFor} onOpenChange={(o) => !o && setExtendFor(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Extend subscription</AlertDialogTitle>
            <AlertDialogDescription>
              Push the current period end forward for{" "}
              <span className="font-medium text-foreground">{extendFor?.email}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-center gap-2 text-sm">
            <Input
              type="number"
              min={1}
              max={365}
              value={extendDays}
              onChange={(e) => setExtendDays(Math.max(1, Math.min(365, Number(e.target.value) || 1)))}
              className="w-24"
            />
            <span className="text-muted-foreground">days</span>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (extendFor) extendMut.mutate({ userId: extendFor.id, days: extendDays });
                setExtendFor(null);
              }}
            >
              Extend
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm dialog (delete / ban / bulk) */}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.kind === "delete" && "Delete this user?"}
              {confirm?.kind === "ban" && (confirm.banned ? "Ban this user?" : "Unban this user?")}
              {confirm?.kind === "bulk-ban" && `Ban ${confirm.ids.length} users?`}
              {confirm?.kind === "bulk-delete" && `Delete ${confirm.ids.length} users?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.kind === "delete" &&
                "This permanently removes the account and all related data. This cannot be undone."}
              {confirm?.kind === "ban" &&
                (confirm.banned
                  ? "The user will be unable to access their account."
                  : "The user will regain access immediately.")}
              {confirm?.kind === "bulk-ban" &&
                "Selected users will be unable to access their accounts."}
              {confirm?.kind === "bulk-delete" &&
                "Selected accounts will be permanently removed. This cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={
                confirm?.kind === "delete" || confirm?.kind === "bulk-delete"
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : ""
              }
              onClick={() => {
                if (!confirm) return;
                if (confirm.kind === "delete") deleteMut.mutate({ userId: confirm.user.id });
                if (confirm.kind === "ban")
                  banMut.mutate({ userId: confirm.user.id, banned: confirm.banned });
                if (confirm.kind === "bulk-ban") runBulkBan(confirm.ids, true);
                if (confirm.kind === "bulk-delete") runBulkDelete(confirm.ids);
                setConfirm(null);
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
